import { useCallback } from 'react';

/**
 * Hook to calculate dropdown position with flip-up logic.
 * When there isn't enough space below the trigger, opens upward instead.
 * Also handles horizontal overflow (align right when near viewport edge).
 * 
 * @param {Object} options
 * @param {number} options.menuHeight - Expected max height of the dropdown menu (default 300)
 * @param {number} options.menuWidth - Expected width of the dropdown menu (default 150)
 * @param {number} options.offset - Gap between trigger and dropdown (default 4)
 * @returns {function} getPosition(triggerRef) => { top, left, right, openUpward, alignRight }
 */
const useDropdownPosition = ({ menuHeight = 300, menuWidth = 150, offset = 4 } = {}) => {
  const getPosition = useCallback((triggerElement) => {
    if (!triggerElement) {
      return { top: 0, left: 0, openUpward: false, alignRight: false };
    }

    const rect = triggerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openUpward = spaceBelow < menuHeight + offset && spaceAbove > spaceBelow;

    let top;
    if (openUpward) {
      top = rect.top - offset;
    } else {
      top = rect.bottom + offset;
    }

    const centerX = rect.left + rect.width / 2;
    const alignRight = centerX + menuWidth / 2 > viewportWidth - 20;
    const alignLeft = centerX - menuWidth / 2 < 20;

    let left;
    let right;
    let transform;

    if (alignRight) {
      left = undefined;
      right = viewportWidth - rect.right;
      transform = undefined;
    } else if (alignLeft) {
      left = rect.left;
      right = undefined;
      transform = undefined;
    } else {
      left = centerX;
      right = undefined;
      transform = 'translateX(-50%)';
    }

    return {
      top,
      left,
      right,
      transform,
      openUpward,
      maxHeight: openUpward ? spaceAbove - offset * 2 : spaceBelow - offset * 2,
    };
  }, [menuHeight, menuWidth, offset]);

  return getPosition;
};

export default useDropdownPosition;
